import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { AlertCircle, User } from 'tabler-icons-react';
import { useLocation } from 'react-router-dom';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';
import { Context as RegistrationAdminDashboardContext } from '../../../providers/RegistrationAdminDashboardProvider';
import AppCard from '../../common/AppCard';
import TableView from '../../common/TableView';
import { useModalState, useSortByFilter } from '../../../helpers/hooks';
import {
  getSpecialRequestsWarnings,
  getTeamLaunchStatus
} from '../../../helpers/teamGeneration';
import SendEmailModal from '../../common/SendEmailModal';
import SyncDivisionTeamsModal from './SyncDivisionTeamsModal';
import TeamsModal from '../teams/TeamsModal';
import TeamDetailsModal from '../teams/TeamDetailsModal';
import { singularPluralFormat } from '../../../helpers/format';
import TeamSyncStatusModal from './TeamSyncStatusModal';
import TeamRebaseConnectionModal from './TeamRebaseConnectionModal';

const TEAM_ACTIONS_ENUM = {
  view: 'VIEW',
  create: 'CREATE',
  edit: 'EDIT',
  emailUsers: 'EMAIL',
  sync: 'SYNC',
  delete: 'DELETE'
};

const TEAM_ACTIONS = [
  {
    section: 1,
    label: 'View',
    value: TEAM_ACTIONS_ENUM.view
  },
  {
    section: 1,
    label: 'Edit',
    value: TEAM_ACTIONS_ENUM.edit
  },
  {
    section: 1,
    label: 'Email Users',
    value: TEAM_ACTIONS_ENUM.emailUsers
  },
  {
    section: 1,
    label: 'Sync status',
    value: 'team_sync_info',
    isVisible: (t) => t.team
  },
  {
    section: 1,
    label: 'Launch Team',
    value: TEAM_ACTIONS_ENUM.sync
  },
  {
    section: 2,
    label: 'Rebase Connection',
    value: 'team_rebase_connection',
    isVisible: (t) => t.team
  },
  {
    section: 2,
    label: 'Delete',
    value: TEAM_ACTIONS_ENUM.delete,
    color: '#C40000'
  }
];

const TABLE_COLUMNS = [
  {
    label: 'Name',
    value: 'name',
    sortable: true
  },
  {
    label: 'Division',
    value: 'division',
    sortable: true
  },
  {
    label: 'Age Requirements',
    value: 'age',
    sortable: true
  },
  {
    label: 'Overall Rating',
    value: 'overall',
    sortable: true
  },
  {
    label: 'Status',
    value: 'status',
    sortable: true
  }
];

const LeagueTeamsView = ({
  isLoading,
  modalAction,
  filters,
  filterState,
  setFilterState,
  regAssociation
}) => {
  const { state: locationState } = useLocation();
  const { state: dashboardState } = useContext(
    RegistrationAdminDashboardContext
  );
  const {
    state,
    fetchRegAssociationDivisionTeams,
    fetchAdminRegSpecialRequests
  } = useContext(RegistrationAdminContext);
  const { state: modalState, onOpenModal, onCloseModal } = useModalState();
  const {
    sortValue,
    isDescendingSort,
    onChangeSortBy,
    customFilterData
  } = useSortByFilter(TABLE_COLUMNS, { storageKey: 'league_teams_list' });

  const divisionTeams = state.regAssociationDivisionTeams.value.filter(
    (t) =>
      (!filterState.pkRegAssociationSeason ||
        t.regAssociationDivision.fkRegAssociationSeason.toString() ===
          filterState?.pkRegAssociationSeason) &&
      (!filterState.pkRegAssociationDivision ||
        t.fkRegAssociationDivision.toString() ===
          filterState?.pkRegAssociationDivision) &&
      (!filterState.teamName ||
        t.name.toLowerCase().includes(filterState.teamName.toLowerCase()))
  );

  const divisionSpecialRequests = state.regSpecialRequests.value.filter(
    (s) =>
      (!filterState.pkRegAssociationSeason ||
        s.regFormSubmission1.regAssociationDivisionOverride?.regAssociationSeason.pkRegAssociationSeason.toString() ===
          filterState.pkRegAssociationSeason ||
        (!s.regFormSubmission1.regAssociationDivisionOverride &&
          s.regFormSubmission1.regAssociationDivisionForm.regAssociationDivision.regAssociationSeason.pkRegAssociationSeason.toString() ===
            filterState.pkRegAssociationSeason)) &&
      (!filterState.pkRegAssociationDivision ||
        s.regFormSubmission1.regAssociationDivisionOverride?.regAssociationSeason.pkRegAssociationSeason.toString() ===
          filterState.pkRegAssociationDivision ||
        (!s.regFormSubmission1.regAssociationDivisionOverride &&
          s.regFormSubmission1.regAssociationDivisionForm.regAssociationDivision.regAssociationSeason.pkRegAssociationSeason.toString() ===
            filterState.pkRegAssociationDivision))
  );

  const regSpecialRequestErrors = getSpecialRequestsWarnings(
    divisionTeams,
    divisionSpecialRequests
  );

  const divisionsWithUnassignedRegPersons = state.regAssociationDivisions.value.filter(
    (d) =>
      (!filterState.pkRegAssociationSeason ||
        d.fkRegAssociationSeason.toString() ===
          filterState?.pkRegAssociationSeason) &&
      (!filterState.pkRegAssociationDivision ||
        d.pkRegAssociationDivision.toString() ===
          filterState?.pkRegAssociationDivision) &&
      d.unassignedRegPersonCount > 0
  );

  const unassignedRegPersonCount = divisionsWithUnassignedRegPersons.reduce(
    (r, c) => r + c.unassignedRegPersonCount,
    0
  );

  const divisionTeamErrors = [
    ...(unassignedRegPersonCount > 0
      ? [
          {
            key: 0,
            message: `${unassignedRegPersonCount} unassigned registrant(s)`,
            additionalErrors:
              divisionsWithUnassignedRegPersons.length > 0
                ? divisionsWithUnassignedRegPersons.map(
                    (d) => `${d.name} - ${d.unassignedRegPersonCount}`
                  )
                : null,
            color: 'red',
            icon: <AlertCircle />
          }
        ]
      : []),
    ...(regSpecialRequestErrors.length > 0
      ? [
          {
            key: 2,
            message: `${regSpecialRequestErrors.length} special request conditions not met`,
            additionalErrors: regSpecialRequestErrors,
            color: 'red',
            icon: <AlertCircle />
          }
        ]
      : [])
  ];

  useEffect(() => {
    if (modalAction === TEAM_ACTIONS_ENUM.create) {
      onOpenModal(modalAction);
    }
  }, [modalAction]);

  useEffect(() => {
    if (locationState?.openModal && !isLoading) {
      setFilterState({
        ...filterState,
        pkRegAssociationDivision:
          locationState.pkRegAssociationDivision?.toString() ?? null
      });
      onOpenModal(TEAM_ACTIONS_ENUM.create);
    }
  }, [locationState?.openModal, isLoading]);

  return (
    <AppCard style={{ flex: 1, padding: 0, height: '100%' }}>
      <TableView
        alerts={divisionTeamErrors}
        columns={TABLE_COLUMNS}
        emptyMessage="No Teams Available"
        filters={filters}
        isDescendingSort={isDescendingSort}
        isLoading={isLoading}
        lastUpdated={state.regAssociationDivisionTeams.lastUpdated}
        onAction={(action, item) => {
          onOpenModal(
            action,
            state.regAssociationDivisionTeams.value.find(
              (s) => s.pkRegAssociationDivisionTeam === item.key
            )
          );
        }}
        onChangeSortBy={onChangeSortBy}
        onRefresh={() => {
          fetchRegAssociationDivisionTeams({
            pkRegAssociation:
              dashboardState.regAssociation.value.pkRegAssociation
          });
          fetchAdminRegSpecialRequests({
            pkRegAssociation:
              dashboardState.regAssociation.value.pkRegAssociation
          });
        }}
        rows={divisionTeams.map((t) => {
          const allRatings = t.regAssociationDivisionTeamPersons
            .map((p) => p.regScoutReportPlayer?.overallScore)
            .filter((r) => !!r);
          const overallRating =
            allRatings.length > 0
              ? Math.round(
                  (allRatings.reduce((a, b) => a + b, 0) / allRatings.length) *
                    10
                ) / 10
              : 0;

          const launchStatus = getTeamLaunchStatus(t);
          const launchStatusSubLabel = launchStatus.getSubLabel
            ? launchStatus.getSubLabel(t.name, t.team)
            : null;

          return {
            key: t.pkRegAssociationDivisionTeam,
            actions: TEAM_ACTIONS.filter((a) => !a.isVisible || a.isVisible(t)),
            columns: [
              {
                key: 1,
                label: t.name,
                weight: 500,
                subLabel: `${
                  t.regAssociationDivisionTeamPersons.filter((p) => p.active)
                    .length
                } Players`,
                onClick: () => onOpenModal(TEAM_ACTIONS_ENUM.edit, t),
                icon: <User size={32} />
              },
              {
                key: 2,
                label: t.regAssociationDivision.name,
                weight: 500,
                subLabel: t.regAssociationDivision.regAssociationSeason.name
              },
              {
                key: 3,
                label: t.regAssociationDivision.isAllAges
                  ? '(All Ages)'
                  : `(${dayjs(new Date()).diff(
                      t.regAssociationDivision.minDob,
                      'year'
                    )} years old and younger)`,
                sortValue: dayjs(new Date()).diff(
                  t.regAssociationDivision.minDob,
                  'year'
                ),
                sortValueType: 'date',
                showOnMobile: true
              },
              {
                key: 4,
                label: overallRating !== 0 ? overallRating : '',
                sortValueType: 'number',
                showOnMobile: true
              },
              {
                key: 5,
                label: launchStatus.label,
                color: launchStatus.color,
                subLabel:
                  launchStatusSubLabel ||
                  (launchStatus.externalWebsitePlayers?.length > 0
                    ? `${singularPluralFormat(
                        launchStatus.externalWebsitePlayers.length,
                        'active player',
                        'active players'
                      )} on website not in team`
                    : null),
                showOnMobile: true
              }
            ]
          };
        })}
        sortBy={sortValue}
        sortFilter={customFilterData}
        tableTitle="Teams"
      />

      <TeamDetailsModal
        isOpen={
          modalState.isOpen && modalState.action === TEAM_ACTIONS_ENUM.view
        }
        onClose={onCloseModal}
        regAssociation={regAssociation}
        regAssociationDivisionTeam={modalState.item}
      />

      <TeamsModal
        fkRegAssociationDivision={filterState.pkRegAssociationDivision}
        fkRegAssociationSeason={filterState.pkRegAssociationSeason}
        isOpen={
          modalState.isOpen &&
          (modalState.action === TEAM_ACTIONS_ENUM.edit ||
            modalState.action === TEAM_ACTIONS_ENUM.delete ||
            modalState.action === TEAM_ACTIONS_ENUM.create)
        }
        onClose={onCloseModal}
        regAssociation={dashboardState.regAssociation.value}
        regAssociationDivisionTeam={modalState.item}
        showDelete={modalState.action === TEAM_ACTIONS_ENUM.delete}
      />

      <SendEmailModal
        isOpen={
          modalState.isOpen &&
          modalState.action === TEAM_ACTIONS_ENUM.emailUsers
        }
        onClose={onCloseModal}
        pkRegAssociation={dashboardState.regAssociation.value?.pkRegAssociation}
        pkRegAssociationDivisionTeam={
          modalState.item?.pkRegAssociationDivisionTeam
        }
      />
      <SyncDivisionTeamsModal
        isOpen={
          modalState.isOpen && modalState.action === TEAM_ACTIONS_ENUM.sync
        }
        onClose={onCloseModal}
        regAssociationDivisionTeams={
          modalState.isOpen &&
          modalState.action === TEAM_ACTIONS_ENUM.sync &&
          modalState.item
            ? [modalState.item]
            : []
        }
      />

      <TeamSyncStatusModal
        isOpen={modalState.isOpen && modalState.action === 'team_sync_info'}
        onClose={onCloseModal}
        regTeam={modalState.item}
      />

      <TeamRebaseConnectionModal
        isOpen={
          modalState.isOpen && modalState.action === 'team_rebase_connection'
        }
        onClose={onCloseModal}
        regAssociation={dashboardState.regAssociation.value}
        regTeam={modalState.item}
      />
    </AppCard>
  );
};

LeagueTeamsView.propTypes = {
  filterState: PropTypes.object,
  filters: PropTypes.array,
  isLoading: PropTypes.bool,
  modalAction: PropTypes.string,
  regAssociation: PropTypes.object,
  setFilterState: PropTypes.func
};

export default LeagueTeamsView;
